/**
 * JobsBrowserWidget — Browse sportsbook job listings from Supabase
 * Reads from: Supabase `jobs` table (populated by scraper pipelines)
 * Tracks: Application status via Supabase `applications` table
 * Supports: Multiple companies (DraftKings now, more sportsbooks later)
 * Features: Real logos, company filters, search, applied tracking, scrape status
 * CSS prefix: jbw-
 */

import { useState, useEffect, useCallback, useMemo } from 'react'
import { Briefcase, RefreshCw, Search, ChevronRight, ChevronDown, ExternalLink, MapPin, Clock, Filter, CheckCircle, XCircle, AlertTriangle, ArrowUpDown, Eye } from 'lucide-react'
import { useAuth, FeatureGate } from '../auth'
import {
  formatNextRunEt,
  formatScheduleEt,
  runtimeFreshnessClass,
  runtimeRunTotals,
  runtimeStatusText,
} from '../lib/jobsRuntimeStatus'
import { jobCompanyLogoSrc } from '../lib/bookLogoRegistry'
import './JobsBrowserWidget.css'
import AbetChipLoader from './AbetChipLoader'

// Supabase config from env
const SUPABASE_URL = (import.meta.env.VITE_APP_CORE_SUPABASE_URL || import.meta.env.VITE_SUPABASE_URL || '').replace(/\/+$/, '')
const SUPABASE_KEY = import.meta.env.VITE_APP_CORE_SUPABASE_ANON_KEY || import.meta.env.VITE_SUPABASE_ANON_KEY || ''
const JOBS_HEALTH_URL = import.meta.env.VITE_JOBS_HEALTH_URL ||
  'https://abet-jobs-watchdog.abetco.workers.dev/jobs-health'

const SB_HEADERS = {
  apikey: SUPABASE_KEY,
  Authorization: `Bearer ${SUPABASE_KEY}`,
  'Content-Type': 'application/json',
}

// Company colors and text fallbacks. Every image URL comes from the audited
// sportsbook master list through jobCompanyLogoSrc().
const COMPANY_META = {
  'bet365':                                      { color: '#FFD600', abbr: '365' },
  'BetMGM':                                      { color: '#b8860b', abbr: 'MG' },
  'Betr':                                        { color: '#00D4AA', abbr: 'BR' },
  'BetRivers (Rush Street Interactive)':         { color: '#003366', abbr: 'BR' },
  'Betway':                                      { color: '#1B5E20', abbr: 'BW' },
  'Caesars':                                     { color: '#8b0000', abbr: 'CZ' },
  'DraftKings':                                  { color: '#00694b', abbr: 'DK' },
  'Fanatics Betting & Gaming':                   { color: '#E53C2E', abbr: 'FN' },
  'FanDuel':                                     { color: '#1493ff', abbr: 'FD' },
  'Fliff':                                       { color: '#6c2bd9', abbr: 'FL' },
  'Hard Rock Bet':                               { color: '#1a1a1a', abbr: 'HR' },
  'Kalshi':                                      { color: '#1a1a1a', abbr: 'KL' },
  'Novig':                                       { color: '#0066cc', abbr: 'NV' },
  'PENN Interactive (ESPN BET / theScore)':      { color: '#cc0000', abbr: 'ES' },
  'PrizePicks':                                  { color: '#7C3AED', abbr: 'PP' },
  'Sleeper':                                     { color: '#6c2bd9', abbr: 'SL' },
  'Kambi':                                       { color: '#1a1a2e', abbr: 'KB' },
  "Bally's Corporation":                         { color: '#e31e24', abbr: 'BA' },
  'Rebet':                                       { color: '#00c853', abbr: 'RB' },
  'Chalk':                                       { color: '#224940', abbr: 'CH' },
  'Dabble':                                      { color: '#6A3DFF', abbr: 'DA' },
  'ProphetX':                                    { color: '#52cea6', abbr: 'PX' },
  'Underdog Fantasy':                            { color: '#FF6B35', abbr: 'UD' },
  'Wynn Resorts':                                { color: '#8B4513', abbr: 'WN' },
  // Additional known sportsbooks for non-scraper context
  Pinnacle:                                      { color: '#cc0000', abbr: 'PN' },
  PointsBet:                                     { color: '#e63946', abbr: 'PB' },
  Fanatics:                                      { color: '#003087', abbr: 'FN' },
  ESPN:                                          { color: '#cc0000', abbr: 'ES' },
  BetOnline:                                     { color: '#3c0', abbr: 'BO' },
  Bovada:                                        { color: '#cc0000', abbr: 'BV' },
  MyBookie:                                      { color: '#e63946', abbr: 'MB' },
}

function getCompanyMeta(company) {
  const fallback = { color: '#555', abbr: company.slice(0, 2).toUpperCase() }
  return { ...(COMPANY_META[company] || fallback), logo: jobCompanyLogoSrc(company) }
}

function timeAgo(iso) {
  if (!iso) return ''
  const ms = Date.now() - new Date(iso).getTime()
  const abs = Math.abs(ms)
  const suffix = ms >= 0 ? 'ago' : 'from now'
  const mins = Math.floor(abs / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ${suffix}`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ${suffix}`
  const days = Math.floor(hrs / 24)
  return `${days}d ${suffix}`
}

function shortDate(iso) {
  if (!iso) return ''
  const d = new Date(iso)
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
  return `${months[d.getMonth()]} ${d.getDate()}`
}

function isRemote(location) {
  return (location || '').toLowerCase().includes('remote')
}

function stripHtml(s) {
  return (s || '')
    .replace(/<\/?[a-zA-Z][^>]*>/g, '')
    .replace(/&lt;\/?[a-zA-Z][^>]*&gt;/g, '')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .trim()
}

// --- Supabase fetchers ---

const CACHE_KEY = 'jbw_jobs_cache'
const CACHE_TTL = 120_000 // 2 min

function getCachedJobs() {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY)
    if (!raw) return null
    const { data, ts } = JSON.parse(raw)
    if (Date.now() - ts > CACHE_TTL) return null
    return data
  } catch { return null }
}

function setCachedJobs(jobs) {
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify({ data: jobs, ts: Date.now() }))
  } catch { /* quota exceeded, ignore */ }
}

async function fetchJobs() {
  if (!SUPABASE_URL || !SUPABASE_KEY) {
    throw new Error('Missing app-core Supabase config (VITE_APP_CORE_SUPABASE_URL / VITE_APP_CORE_SUPABASE_ANON_KEY)')
  }

  const allRows = []
  const pageSize = 500
  let from = 0

  while (true) {
    const to = from + pageSize - 1
    const url = `${SUPABASE_URL}/rest/v1/jobs?select=id,company,title,url,location,is_active,scraped_at,posted_at,first_seen_at,last_seen_at&order=posted_at.desc.nullslast,id.desc`

    const resp = await fetch(url, {
      headers: { ...SB_HEADERS, Range: `${from}-${to}` },
      signal: AbortSignal.timeout(15000),
    })

    if (!resp.ok) {
      const txt = await resp.text()
      throw new Error(`Supabase ${resp.status}: ${txt}`)
    }

    const chunk = await resp.json()
    allRows.push(...chunk)
    if (chunk.length < pageSize) break
    from += pageSize
  }

  // Stable pagination plus an ID guard prevents page-boundary duplicates from
  // producing duplicate React keys when many jobs share the same posted_at.
  return Array.from(new Map(allRows.map((job) => [job.id ?? `${job.company}|${job.url}`, job])).values())
}

async function fetchJobDescription(jobId) {
  const resp = await fetch(
    `${SUPABASE_URL}/rest/v1/jobs?id=eq.${jobId}&select=id,description`,
    { headers: SB_HEADERS, signal: AbortSignal.timeout(10000) }
  )
  if (!resp.ok) return null
  const rows = await resp.json()
  return rows[0]?.description || null
}

function getAllKnownCompanies() {
  // Full provider names the scraper produces — exclude short-name aliases
  const aliases = new Set(['Pinnacle', 'PointsBet', 'Fanatics', 'ESPN', 'BetOnline', 'Bovada', 'MyBookie', 'HardRock', 'BetRivers', 'Underdog', 'Wynn'])
  return Object.keys(COMPANY_META).filter(k => !aliases.has(k)).sort()
}

async function fetchApplications(userId) {
  try {
    let url = `${SUPABASE_URL}/rest/v1/applications?select=id,company,job_url,status,applied_at,notes`
    // If userId provided, filter by it (graceful: if column doesn't exist, the query
    // will fail and we fall back to empty array)
    if (userId) {
      url += `&user_id=eq.${userId}`
    }
    const resp = await fetch(url, { headers: SB_HEADERS, signal: AbortSignal.timeout(10000) })
    if (!resp.ok) return []
    return await resp.json()
  } catch {
    return []
  }
}

async function fetchScrapeStatus() {
  try {
    const resp = await fetch(
      `${SUPABASE_URL}/rest/v1/job_scrape_runs?select=id,provider,status,job_count,error_summary,started_at,trigger_source&order=id.desc&limit=60`,
      { headers: SB_HEADERS, signal: AbortSignal.timeout(10000) }
    )
    if (!resp.ok) return []
    return await resp.json()
  } catch {
    return []
  }
}

async function fetchJobsHealth() {
  try {
    const resp = await fetch(JOBS_HEALTH_URL, {
      headers: { Accept: 'application/json' },
      signal: AbortSignal.timeout(10000),
    })
    if (!resp.ok) return null
    return await resp.json()
  } catch {
    return null
  }
}

async function upsertApplication(company, jobUrl, jobTitle, status, userId) {
  // Build body — include user_id if available (column may not exist yet)
  const body = { company, job_url: jobUrl, job_title: jobTitle, status }
  let conflictKey = 'company,job_url'
  if (userId) {
    body.user_id = userId
    conflictKey = 'user_id,company,job_url'
  }

  const resp = await fetch(
    `${SUPABASE_URL}/rest/v1/applications?on_conflict=${conflictKey}`,
    {
      method: 'POST',
      headers: { ...SB_HEADERS, Prefer: 'resolution=merge-duplicates,return=representation' },
      body: JSON.stringify(body),
      signal: AbortSignal.timeout(10000),
    }
  )
  if (!resp.ok) {
    const txt = await resp.text()
    // If user_id column doesn't exist, retry without it
    if (userId && (txt.includes('user_id') || txt.includes('column'))) {
      return upsertApplication(company, jobUrl, jobTitle, status, null)
    }
    throw new Error(`Failed to save: ${txt}`)
  }
  return await resp.json()
}

async function deleteApplication(id) {
  const resp = await fetch(
    `${SUPABASE_URL}/rest/v1/applications?id=eq.${id}`,
    { method: 'DELETE', headers: SB_HEADERS, signal: AbortSignal.timeout(10000) }
  )
  if (!resp.ok) throw new Error('Failed to remove application')
}

// --- Company Logo Component ---
function CompanyLogo({ company, size = 42 }) {
  const meta = getCompanyMeta(company)
  const [imgError, setImgError] = useState(false)

  if (meta.logo && !imgError) {
    return (
      <span className="jbw-company-badge jbw-company-badge-logo" title={company}>
        <img
          src={meta.logo}
          alt={company}
          width={size - 6}
          height={size - 6}
          onError={() => setImgError(true)}
        />
      </span>
    )
  }

  return (
    <span
      className="jbw-company-badge"
      style={{ background: meta.color }}
      title={company}
    >
      {meta.abbr}
    </span>
  )
}

// --- Application Status Selector ---
const APP_STATUSES = [
  { value: 'applied', label: 'Applied', color: '#58a6ff' },
  { value: 'interviewing', label: 'Interviewing', color: '#d29922' },
  { value: 'offer', label: 'Offer', color: '#3fb950' },
  { value: 'rejected', label: 'Rejected', color: '#f85149' },
  { value: 'archived', label: 'Archived', color: '#8b949e' },
]

function AppliedBadge({ status }) {
  const s = APP_STATUSES.find(a => a.value === status) || APP_STATUSES[0]
  return (
    <span className="jbw-applied-badge" style={{ borderColor: s.color, color: s.color }}>
      <CheckCircle size={10} /> {s.label}
    </span>
  )
}

// --- Main Widget ---
export default function JobsBrowserWidget() {
  const { user, isAuthenticated } = useAuth()
  const [jobs, setJobs] = useState([])
  const [allCompanies, setAllCompanies] = useState([])
  const [applications, setApplications] = useState([])
  const [scrapeRuns, setScrapeRuns] = useState([])
  const [runtimeHealth, setRuntimeHealth] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [lastFetch, setLastFetch] = useState(null)
const [expanded, setExpanded] = useState(true)
  const [showScrapeStatus, setShowScrapeStatus] = useState(false)
  // COLLAPSE DEFAULTS 2026-08-10 (operator): both panels start CLOSED so the board is
  // the first thing on screen. Any active selection is echoed in the collapsed header,
  // so a filter can never be hiding silently.
  const [companiesExpanded, setCompaniesExpanded] = useState(false)
  const [filtersExpanded, setFiltersExpanded] = useState(false)

  // Filters
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCompanies, setSelectedCompanies] = useState(new Set())
  const [activeFilter, setActiveFilter] = useState('active') // 'active' | 'inactive' | 'applied'
  const [remoteOnly, setRemoteOnly] = useState(false)
  const [expandedJobs, setExpandedJobs] = useState(new Set())
  const [sortBy, setSortBy] = useState('newest')

  // Description lazy loading
  const [jobDescriptions, setJobDescriptions] = useState(new Map())
  const [loadingDescriptions, setLoadingDescriptions] = useState(new Set())

  // Build application lookup: job_url -> application record
  const appByUrl = useMemo(() => {
    const map = {}
    for (const a of applications) map[a.job_url] = a
    return map
  }, [applications])

  const loadAll = useCallback(async (forceRefresh = false) => {
    setLoading(true)
    setError(null)

    // Show cached data immediately
    if (!forceRefresh) {
      const cached = getCachedJobs()
      if (cached && cached.length > 0) {
        setJobs(cached)
        setLoading(false)
      }
    }

    try {
      const fetches = [fetchJobs(), fetchScrapeStatus(), fetchJobsHealth()]
      if (isAuthenticated && user?.id) {
        fetches.push(fetchApplications(user.id))
      } else {
        fetches.push(Promise.resolve([]))
      }
      const [jobData, scrapeData, healthData, appData] = await Promise.all(fetches)
      setCachedJobs(jobData)
      setJobs(jobData)
      const companies = getAllKnownCompanies()
      setAllCompanies(companies)
      setApplications(appData)
      setScrapeRuns(scrapeData)
      setRuntimeHealth(healthData)
      setLastFetch(new Date())
      if (selectedCompanies.size === 0) {
        setSelectedCompanies(new Set([...jobData.map(j => j.company), ...companies]))
      }
    } catch (err) {
      if (!getCachedJobs()) {
        setError(err.message)
      }
    } finally {
      setLoading(false)
    }
  }, [isAuthenticated, user?.id])

  useEffect(() => { loadAll() }, [loadAll])

  // --- Application handlers ---
  const handleMarkApplied = async (job, status = 'applied') => {
    try {
      await upsertApplication(job.company, job.url, job.title, status, user?.id || null)
      // Refresh applications
      const updated = await fetchApplications(user?.id || null)
      setApplications(updated)
    } catch (err) {
      alert(`Error: ${err.message}`)
    }
  }

  const handleRemoveApplication = async (appId) => {
    try {
      await deleteApplication(appId)
      const updated = await fetchApplications(user?.id || null)
      setApplications(updated)
    } catch (err) {
      alert(`Error: ${err.message}`)
    }
  }

  // --- Derived data ---
  const companies = useMemo(() => {
    const map = {}
    for (const j of jobs) {
      if (!map[j.company]) map[j.company] = { total: 0, active: 0 }
      map[j.company].total++
      if (j.is_active) map[j.company].active++
    }
    // Merge in all known companies (even those with 0 recent jobs)
    for (const name of allCompanies) {
      if (!map[name]) map[name] = { total: 0, active: 0 }
    }
    return Object.entries(map)
      .sort((a, b) => b[1].active - a[1].active)
      .map(([name, counts]) => ({ name, ...counts }))
  }, [jobs, allCompanies])

  const filtered = useMemo(() => {
    const q = searchTerm.toLowerCase().trim()
    const result = jobs.filter(j => {
      if (selectedCompanies.size > 0 && !selectedCompanies.has(j.company)) return false

      if (activeFilter === 'active' && !j.is_active) return false
      if (activeFilter === 'inactive' && j.is_active) return false
      if (activeFilter === 'applied' && !appByUrl[j.url]) return false

      if (remoteOnly && !isRemote(j.location)) return false
      if (q) {
        const hay = `${j.company} ${j.title} ${j.location || ''}`.toLowerCase()
        if (!hay.includes(q)) return false
      }
      return true
    })

    // Sort
    result.sort((a, b) => {
      switch (sortBy) {
        case 'newest':
          return (b.posted_at || b.first_seen_at || '').localeCompare(a.posted_at || a.first_seen_at || '')
        case 'oldest':
          return (a.posted_at || a.first_seen_at || '').localeCompare(b.posted_at || b.first_seen_at || '')
        case 'company':
          return a.company.localeCompare(b.company) || a.title.localeCompare(b.title)
        case 'title':
          return a.title.localeCompare(b.title)
        default:
          return 0
      }
    })

    return result
  }, [jobs, searchTerm, selectedCompanies, activeFilter, remoteOnly, appByUrl, sortBy])

  const toggleCompany = (name) => {
    setSelectedCompanies(prev => {
      const next = new Set(prev)
      if (next.has(name)) next.delete(name)
      else next.add(name)
      return next
    })
  }

  const selectAllCompanies = () => {
    setSelectedCompanies(new Set(companies.map(c => c.name)))
  }

  const toggleJobExpand = (id) => {
    setExpandedJobs(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
    // Lazy-load description when expanding
    if (!jobDescriptions.has(id) && !loadingDescriptions.has(id)) {
      setLoadingDescriptions(prev => new Set(prev).add(id))
      fetchJobDescription(id).then(desc => {
        setJobDescriptions(prev => new Map(prev).set(id, desc))
        setLoadingDescriptions(prev => {
          const next = new Set(prev)
          next.delete(id)
          return next
        })
      })
    }
  }

  // Stats
  const totalActive = jobs.filter(j => j.is_active).length
  const totalInactive = jobs.filter(j => !j.is_active).length
  const totalApplied = applications.length
  const remoteCount = filtered.filter(j => isRemote(j.location)).length

  // Scrape health
  const lastScrape = scrapeRuns[0] || null
  const lastSuccessfulScrape = scrapeRuns.find(r => r.status === 'success')
  const latestJobTimestamp = jobs.length > 0 ? jobs[0].scraped_at : null

  // Freshness calculation
  const lastScrapeReceipt = runtimeHealth?.last_run_end || lastSuccessfulScrape?.started_at
  const lastScrapeTime = lastScrapeReceipt ? new Date(lastScrapeReceipt) : null
  const scrapeAgeMs = lastScrapeTime ? Date.now() - lastScrapeTime.getTime() : null
  const ageFreshness = scrapeRuns.length === 0 ? 'nodata'
    : scrapeAgeMs === null ? 'unknown'
    : scrapeAgeMs < 3600000 ? 'fresh'
    : scrapeAgeMs < 21600000 ? 'recent'
    : scrapeAgeMs < 43200000 ? 'aging'
    : 'stale'
  const scrapeFreshness = runtimeFreshnessClass(runtimeHealth, ageFreshness)
  const nextRunLabel = formatNextRunEt(runtimeHealth?.next_run_et)
  const scheduleLabel = formatScheduleEt(runtimeHealth?.schedule)

  // The launcher manifest is the atomic receipt for one complete run. Summing a time
  // window of provider rows mixed adjacent runs and once displayed 1,709/43 for an
  // actual 880-job/24-provider run.
  const lastRunTotals = runtimeRunTotals(runtimeHealth)

  return (
    <div className="jbw-widget">
      {/* Header */}
      <div className="jbw-header" onClick={() => setExpanded(!expanded)}>
        <div className="jbw-header-left">
          <Briefcase size={18} className="jbw-header-icon" />
          <h3 className="jbw-title">Sportsbook Jobs</h3>
          <span className="jbw-badge">{totalActive} active</span>
          {totalApplied > 0 && (
            <span className="jbw-badge jbw-badge-applied">{totalApplied} applied</span>
          )}
          {companies.length > 1 && (
            <span className="jbw-badge jbw-badge-muted">{companies.length} companies</span>
          )}
        </div>
        <div className="jbw-header-right">
          {lastFetch && (
            <span className="jbw-last-fetch">
              Updated {lastFetch.toLocaleTimeString()}
            </span>
          )}
          <button
            className="jbw-refresh-btn"
            onClick={(e) => { e.stopPropagation(); loadAll(true) }}
            disabled={loading}
            title="Refresh"
          >
            <RefreshCw size={14} className={loading ? 'jbw-spin' : ''} />
          </button>
          {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </div>
      </div>

      {expanded && (
        <div className="jbw-body">
          {error && (
            <div className="jbw-error">
              <span>Error: {error}</span>
              <button onClick={loadAll}>Retry</button>
            </div>
          )}

          {/* Scrape Freshness Banner */}
          <div className={`jbw-freshness-banner jbw-freshness-${scrapeFreshness}`}>
            <div className="jbw-freshness-main">
              <div className="jbw-freshness-left">
                <span className={`jbw-freshness-dot jbw-dot-${scrapeFreshness}`} />
                <span className="jbw-freshness-label">
                  {runtimeHealth ? runtimeStatusText(runtimeHealth) :
                   scrapeFreshness === 'fresh' ? 'Data is fresh' :
                   scrapeFreshness === 'recent' ? 'Data is recent' :
                   scrapeFreshness === 'aging' ? 'Data is aging' :
                   scrapeFreshness === 'stale' ? 'Data may be stale' :
                   scrapeFreshness === 'nodata' ? 'Scraper status unavailable' :
                   'Freshness unknown'}
                </span>
                {lastScrapeTime && (
                  <span className="jbw-freshness-time">
                    Last scraped {timeAgo(lastScrapeTime.toISOString())}
                    <span className="jbw-freshness-exact" title={lastScrapeTime.toISOString()}>
                      ({lastScrapeTime.toLocaleString()})
                    </span>
                  </span>
                )}
              </div>
              <div className="jbw-freshness-right">
                {lastRunTotals && (
                  <span className="jbw-freshness-count" title={`${lastRunTotals.providers} providers reported in the last run`}>
                    {lastRunTotals.jobs} jobs · {lastRunTotals.providers} providers
                    {lastRunTotals.failed > 0 && (
                      <span style={{ color: '#FF0055', fontWeight: 700 }}
                            title={`Failing: ${lastRunTotals.failedNames.join(', ')}`}>
                        {' '}· {lastRunTotals.failedNames.slice(0, 3).join(', ')} failed
                        {lastRunTotals.failedNames.length > 3 && ` +${lastRunTotals.failedNames.length - 3}`}
                      </span>
                    )}
                  </span>
                )}
                <span className="jbw-freshness-next"
                      title={scheduleLabel || 'Runtime schedule receipt is currently unavailable.'}>
                  {nextRunLabel ? `Next scheduled ${nextRunLabel}` : 'Schedule status unavailable'}
                </span>
                <button
                  className="jbw-freshness-expand"
                  onClick={() => setShowScrapeStatus(!showScrapeStatus)}
                  title="Show scrape history"
                >
                  {lastScrape?.status === 'error' && <AlertTriangle size={12} className="jbw-scrape-warn" />}
                  History {showScrapeStatus ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                </button>
              </div>
            </div>

            {showScrapeStatus && (
              <div className="jbw-scrape-details">
                <div className="jbw-scrape-runs-label">Recent scrape runs:</div>
                {scrapeRuns.map(r => (
                  <div key={r.id} className={`jbw-scrape-run ${r.status}`}>
                    <span className="jbw-scrape-run-icon">
                      {r.status === 'success' ? <CheckCircle size={11} /> :
                       r.status === 'error' ? <XCircle size={11} /> :
                       <Clock size={11} />}
                    </span>
                    <span className="jbw-scrape-run-provider">{r.provider}</span>
                    <span className="jbw-scrape-run-status">{r.status}</span>
                    {r.job_count > 0 && <span className="jbw-scrape-run-count">{r.job_count} jobs</span>}
                    <span className="jbw-scrape-run-time">{new Date(r.started_at).toLocaleString()}</span>
                    <span className="jbw-scrape-run-source">{r.trigger_source}</span>
                    {r.error_summary && (
                      <span className="jbw-scrape-run-error" title={r.error_summary}>
                        {r.error_summary.length > 80 ? r.error_summary.slice(0, 80) + '...' : r.error_summary}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* LAYOUT 2026-08-10: the two collapsible headers each occupied a full row,
              leaving a wide empty gap to the right of both. They now share one flex row and
              wrap only on narrow screens. Their expanded panels still render full-width
              underneath, so opening one does not squeeze the other. */}
          <div className="jbw-panel-row" style={{ display: 'flex', gap: 18, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <div className="jbw-companies" style={{ flex: companiesExpanded ? '1 1 100%' : '1 1 260px', minWidth: 240 }}>
            <button
              className="jbw-companies-header"
              onClick={() => setCompaniesExpanded(!companiesExpanded)}
              style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, cursor: 'pointer', padding: '4px 0' }}
            >
              <span className="jbw-filter-label"><Filter size={13} /> Companies:</span>
              <ChevronRight
                size={13}
                className={companiesExpanded ? 'jbw-rotate-90' : ''}
                style={{ transition: 'transform 0.2s', flexShrink: 0 }}
              />
              {/* echo the selection while collapsed */}
              {!companiesExpanded && (
                <span style={{ fontSize: 12, opacity: 0.7, marginLeft: 2 }}>
                  {selectedCompanies.size === 0
                    ? `all ${companies.length}`
                    : <span style={{ color: 'var(--accent-secondary, #FDB515)', fontWeight: 700 }}>
                        {selectedCompanies.size} selected
                      </span>}
                </span>
              )}
            </button>
            {companiesExpanded && (
              <div className="jbw-companies-list">
                {companies.map(c => {
                  const meta = getCompanyMeta(c.name)
                  const active = selectedCompanies.has(c.name)
                  return (
                    <button
                      key={c.name}
                      className={`jbw-company-chip ${active ? 'active' : ''}`}
                      onClick={() => toggleCompany(c.name)}
                      style={active ? { borderColor: meta.color, background: `${meta.color}22` } : {}}
                    >
                      <CompanyLogo company={c.name} size={24} />
                      <span className="jbw-chip-name">{c.name}</span>
                      <span className="jbw-chip-count">{c.active}</span>
                    </button>
                  )
                })}
                {companies.length > 1 && selectedCompanies.size < companies.length && (
                  <button className="jbw-company-chip jbw-select-all" onClick={selectAllCompanies}>
                    Select All
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Search & Filters header — shares the row above with Companies */}
          <div style={{ flex: filtersExpanded ? '1 1 100%' : '1 1 260px', minWidth: 240 }}>
          <button
            className="jbw-companies-header"
            onClick={() => setFiltersExpanded(!filtersExpanded)}
            style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, cursor: 'pointer', padding: '4px 0' }}
          >
            <span className="jbw-filter-label"><Search size={13} /> Search &amp; Filters:</span>
            <ChevronRight
              size={13}
              className={filtersExpanded ? 'jbw-rotate-90' : ''}
              style={{ transition: 'transform 0.2s', flexShrink: 0 }}
            />
            {!filtersExpanded && (
              <span style={{ fontSize: 12, opacity: 0.7, marginLeft: 2 }}>
                {[searchTerm.trim() ? `“${searchTerm.trim()}”` : null,
                  activeFilter !== 'active' ? activeFilter : null,
                  remoteOnly ? 'remote only' : null].filter(Boolean).length === 0
                  ? 'none'
                  : <span style={{ color: 'var(--accent-secondary, #FDB515)', fontWeight: 700 }}>
                      {[searchTerm.trim() ? `“${searchTerm.trim()}”` : null,
                        activeFilter !== 'active' ? activeFilter : null,
                        remoteOnly ? 'remote only' : null].filter(Boolean).join(' · ')}
                    </span>}
              </span>
            )}
          </button>
          </div>
          </div>{/* /jbw-panel-row */}

          <div className="jbw-filters" style={{ display: filtersExpanded ? undefined : 'none' }}>
            <div className="jbw-search-wrap">
              <Search size={14} className="jbw-search-icon" />
              <input
                className="jbw-search"
                type="text"
                placeholder="Search jobs by title, company, location, description..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button className="jbw-search-clear" onClick={() => setSearchTerm('')}>×</button>
              )}
            </div>

            <div className="jbw-filter-toggles">
              <button
                className={`jbw-filter-btn ${activeFilter === 'active' ? 'active' : ''}`}
                onClick={() => setActiveFilter('active')}
              >
                Active ({totalActive})
              </button>
              <button
                className={`jbw-filter-btn ${activeFilter === 'inactive' ? 'active' : ''}`}
                onClick={() => setActiveFilter('inactive')}
              >
                Inactive ({totalInactive})
              </button>
              <button
                className={`jbw-filter-btn ${activeFilter === 'applied' ? 'active' : ''}`}
                onClick={() => setActiveFilter('applied')}
              >
                <CheckCircle size={12} /> Applied ({totalApplied})
              </button>
              <button
                className={`jbw-filter-btn ${remoteOnly ? 'active' : ''}`}
                onClick={() => setRemoteOnly(!remoteOnly)}
              >
                <MapPin size={12} /> Remote {remoteOnly ? `(${remoteCount})` : ''}
              </button>
            </div>
          </div>

          {/* Results count + sort */}
          <div className="jbw-results-bar">
            <span className="jbw-results-count">
              Showing {filtered.length} of {jobs.length} jobs
            </span>
            <div className="jbw-sort-wrap">
              <ArrowUpDown size={12} />
              <select
                className="jbw-sort-select"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                <option value="newest">Posted Date (newest)</option>
                <option value="oldest">Posted Date (oldest)</option>
                <option value="company">Company A-Z</option>
                <option value="title">Title A-Z</option>
              </select>
            </div>
          </div>

          {/* Job list */}
          {/* BRAND 2026-08-10: the generic RefreshCw spinner is replaced by the ABET
              alpha chip, so the loading state is our mark rather than a stock icon. */}
          {loading && jobs.length === 0 ? (
            <AbetChipLoader message="Loading jobs from Supabase…" />
          ) : filtered.length === 0 ? (
            <div className="jbw-empty">
              No jobs match your filters. Try adjusting search or company selection.
            </div>
          ) : (
            <div className="jbw-job-list">
              {filtered.map(job => {
                const isExpanded = expandedJobs.has(job.id)
                const remote = isRemote(job.location)
                const app = appByUrl[job.url]

                return (
                  <div key={job.id} className={`jbw-job-card ${app ? 'jbw-job-applied' : ''}`}>
                    <div className="jbw-job-header" onClick={() => toggleJobExpand(job.id)}>
                      <div className="jbw-job-left">
                        <CompanyLogo company={job.company} />
                        <div className="jbw-job-info">
                          <div className="jbw-job-title-row">
                            <span className="jbw-job-title">{job.title}</span>
                            {app && <AppliedBadge status={app.status} />}
                          </div>
                          <div className="jbw-job-meta">
                            <span className="jbw-job-company">{job.company}</span>
                            {job.location && (
                              <span className="jbw-job-location">
                                <MapPin size={11} /> {job.location}
                              </span>
                            )}
                            {remote && <span className="jbw-remote-tag">REMOTE</span>}
                            {job.first_seen_at && (Date.now() - new Date(job.first_seen_at).getTime()) < 48 * 60 * 60 * 1000 && (
                              <span className="jbw-new-badge">NEW</span>
                            )}
                            {job.posted_at && (
                              <span className="jbw-job-discovered" title={`Posted: ${new Date(job.posted_at).toLocaleString()}`}>
                                <Clock size={11} /> Posted {shortDate(job.posted_at)}
                              </span>
                            )}
                            {job.first_seen_at && (
                              <span className="jbw-job-discovered" title={`First found by scraper: ${new Date(job.first_seen_at).toLocaleString()}`}>
                                <Eye size={11} /> First seen {shortDate(job.first_seen_at)}
                              </span>
                            )}
                            <span className="jbw-job-time">
                              <Clock size={11} /> Last seen {timeAgo(job.last_seen_at || job.scraped_at)}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="jbw-job-right">
                        <a
                          href={job.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="jbw-open-btn"
                          onClick={(e) => e.stopPropagation()}
                          title="Open listing"
                        >
                          <ExternalLink size={13} /> Open
                        </a>
                        {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                      </div>
                    </div>

                    {isExpanded && (
                      <div className="jbw-job-expanded">
                        {/* Application controls — gated behind auth */}
                        <FeatureGate requiredTier="free" fallbackMessage="Sign in to track your job applications">
                        <div className="jbw-app-controls">
                          {!app ? (
                            <div className="jbw-app-actions">
                              <span className="jbw-app-label">Track application:</span>
                              {APP_STATUSES.slice(0, 3).map(s => (
                                <button
                                  key={s.value}
                                  className="jbw-app-status-btn"
                                  style={{ borderColor: s.color, color: s.color }}
                                  onClick={() => handleMarkApplied(job, s.value)}
                                >
                                  {s.label}
                                </button>
                              ))}
                            </div>
                          ) : (
                            <div className="jbw-app-current">
                              <span className="jbw-app-label">Status:</span>
                              <div className="jbw-app-status-group">
                                {APP_STATUSES.map(s => (
                                  <button
                                    key={s.value}
                                    className={`jbw-app-status-btn ${app.status === s.value ? 'active' : ''}`}
                                    style={app.status === s.value ? { background: `${s.color}22`, borderColor: s.color, color: s.color } : {}}
                                    onClick={() => handleMarkApplied(job, s.value)}
                                  >
                                    {s.label}
                                  </button>
                                ))}
                              </div>
                              <button
                                className="jbw-app-remove"
                                onClick={() => handleRemoveApplication(app.id)}
                                title="Remove tracking"
                              >
                                <XCircle size={13} /> Remove
                              </button>
                              {app.applied_at && (
                                <span className="jbw-app-date">
                                  Tracked {timeAgo(app.applied_at)}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                        </FeatureGate>

                        {/* Description */}
                        {(() => {
                          const lazyDesc = jobDescriptions.get(job.id)
                          const isLoading = loadingDescriptions.has(job.id)
                          const desc = lazyDesc ?? job.description // fallback to inline if somehow present
                          if (isLoading) {
                            return <div className="jbw-job-description jbw-loading">Loading description...</div>
                          }
                          if (desc) {
                            return (
                              <div className="jbw-job-description">
                                <div className="jbw-desc-content">
                                  {(() => {
                                    const clean = stripHtml(desc)
                                    return clean.length > 2000
                                      ? clean.slice(0, 2000) + '...'
                                      : clean
                                  })()}
                                </div>
                                <div className="jbw-desc-footer">
                                  <span className="jbw-desc-chars">{desc.length.toLocaleString()} chars</span>
                                  <a
                                    href={job.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="jbw-full-listing-link"
                                  >
                                    View Full Listing <ExternalLink size={11} />
                                  </a>
                                </div>
                              </div>
                            )
                          }
                          return (
                            <div className="jbw-job-description jbw-no-desc">
                              No description available. <a href={job.url} target="_blank" rel="noopener noreferrer">View on company site</a>
                            </div>
                          )
                        })()}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
